import React, { useCallback, useEffect, useRef, useState } from 'react';
import { User } from '../../types/auth';
import { userService, CreateUserDto, UpdateUserDto } from '../../services/userService';
import { asOneOf, useUrlState, useUrlWriter } from '../../hooks/useUrlState';
import { Dialog, Page } from '../ui';
import { UserList } from './UserList';
import { UserForm, USER_FORM_TITLE_ID } from './UserForm';

const VIEWS = ['list', 'create', 'edit'] as const;
type ViewMode = (typeof VIEWS)[number];

/* One codec per parameter, made once: the URL state reads them on every render */
const VIEW = asOneOf(VIEWS);

export const UserManagement: React.FC = () => {
  // Which form is open, and for whom, is in the address bar; what has been
  // typed into that form is not
  const [view] = useUrlState<ViewMode>('view', 'list', VIEW);
  const [userId] = useUrlState('userId', '');
  const write = useUrlWriter();

  const [known, setKnown] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // A clicked row hands over the whole user; a link carries only an id
  const selectedUser = known && known.id === userId ? known : null;

  /* Which form is open and whose it is always move together, as one change */
  const show = useCallback(
    (mode: ViewMode, user: User | null) => {
      // The cached user is only ever read when its id matches the one in the
      // URL, so closing the form need not clear it — and must not, or the gap
      // before the new URL arrives would look like a link to a stranger
      if (user) setKnown(user);
      write({ view: mode === 'list' ? null : mode, userId: user?.id ?? null });
      setError(null);
    },
    [write],
  );

  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (view !== 'edit' || !userId || selectedUser || asked.current === userId) return;
    asked.current = userId;
    let alive = true;
    (async () => {
      try {
        const user = await userService.getUserById(userId);
        if (!alive) return;
        // Someone who has since been removed opens the list instead
        if (user) setKnown(user);
        else show('list', null);
      } catch {
        if (alive) show('list', null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [view, userId, selectedUser, show]);

  const handleCreateUser = () => show('create', null);

  const handleEditUser = (user: User) => show('edit', user);

  const handleCancel = () => {
    if (isLoading) return;
    show('list', null);
  };

  const handleSubmit = async (data: CreateUserDto | UpdateUserDto) => {
    try {
      setIsLoading(true);
      setError(null);

      if (view === 'create') {
        await userService.createUser(data as CreateUserDto);
      } else if (view === 'edit' && selectedUser) {
        await userService.updateUser(selectedUser.id, data as UpdateUserDto);
      }

      // Refresh the user list
      setRefreshTrigger(prev => prev + 1);
      show('list', null);
    } catch (err: any) {
      console.error('Error submitting user form:', err);
      const message = err.response?.data?.message;
      setError(Array.isArray(message) ? message.join(', ') : message || 'Failed to save user');
    } finally {
      setIsLoading(false);
    }
  };

  // An edit link waits behind the list until its user has arrived, so the form
  // never opens empty and reads as a new one
  const formOpen = view === 'create' || (view === 'edit' && !!selectedUser);

  return (
    <Page>
      <UserList onEditUser={handleEditUser} onCreateUser={handleCreateUser} refreshTrigger={refreshTrigger} />

      <Dialog
        open={formOpen}
        onClose={handleCancel}
        labelledBy={USER_FORM_TITLE_ID}
        handle
        className="max-h-[92vh] sm:max-h-[84vh] sm:max-w-lg"
      >
        {formOpen && (
          <UserForm
            key={selectedUser?.id ?? 'new'}
            user={selectedUser || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
            error={error}
          />
        )}
      </Dialog>
    </Page>
  );
};
